/**
 * Alerts API Endpoints
 * User notification and alert management
 */

import apiClient from '../client';

// Types
export interface AlertData {
  opportunity_type?: string;
  annual_gain?: number;
  from_type?: string;
  to_type?: string;
  goal_id?: number;
  goal_name?: string;
  days_remaining?: number;
  progress_percentage?: number;
  milestone?: number;
  current_amount?: number;
  target_amount?: number;
  current_spending?: number;
  budget?: number;
  percentage?: number;
  week_number?: number;
  year?: number;
  goals_summary?: Array<{
    id: number;
    name: string;
    progress: number;
    target: number;
    current: number;
    days_remaining: number | null;
  }>;
  savings_this_week?: number;
  insight?: string;
  youtube_recommendation?: {
    video_id: string;
    title: string;
    topic: string;
    url: string;
  };
  expiration_date?: string;
}

export interface Alert {
  id: number;
  type: string;
  severity: 'success' | 'warning' | 'info' | 'error';
  title: string;
  message: string;
  data: AlertData;
  read: boolean;
  icon: string;
  color: string;
  action_url: string | null;
  action_label: string | null;
  created_at: string;
}

export interface AlertsResponse {
  success: boolean;
  alerts: Alert[];
  unread_count: number;
  total_count: number;
}

export interface AlertCountResponse {
  success: boolean;
  unread_count: number;
  total_count: number;
}

export interface AlertResponse {
  success: boolean;
  alert: Alert;
}

export interface AlertMessageResponse {
  success: boolean;
  message: string;
}

export interface GenerateAlertsResponse {
  success: boolean;
  alerts_created: number;
  alerts: Alert[];
}

export const alertsApi = {
  /**
   * GET /alerts
   * Get all active alerts for current user
   */
  getAlerts: async (params?: {
    unread_only?: boolean;
    limit?: number;
  }): Promise<AlertsResponse> => {
    const response = await apiClient.get<AlertsResponse>('/alerts', { params });
    return response.data;
  },

  /**
   * GET /alerts/count
   * Get alert counts
   */
  getCount: async (): Promise<AlertCountResponse> => {
    const response = await apiClient.get<AlertCountResponse>('/alerts/count');
    return response.data;
  },

  /**
   * PATCH /alerts/:id/read
   * Mark alert as read
   */
  markAsRead: async (alertId: number): Promise<AlertResponse> => {
    const response = await apiClient.patch<AlertResponse>(
      `/alerts/${alertId}/read`
    );
    return response.data;
  },

  /**
   * POST /alerts/read_all
   * Mark all alerts as read
   */
  markAllAsRead: async (): Promise<AlertMessageResponse> => {
    const response = await apiClient.post<AlertMessageResponse>(
      '/alerts/read_all'
    );
    return response.data;
  },

  /**
   * DELETE /alerts/:id
   * Dismiss an alert
   */
  dismiss: async (alertId: number): Promise<AlertMessageResponse> => {
    const response = await apiClient.delete<AlertMessageResponse>(
      `/alerts/${alertId}`
    );
    return response.data;
  },

  /**
   * DELETE /alerts
   * Dismiss all alerts
   */
  dismissAll: async (): Promise<AlertMessageResponse> => {
    const response = await apiClient.delete<AlertMessageResponse>('/alerts');
    return response.data;
  },

  /**
   * POST /alerts/generate
   * Manually trigger alert generation
   */
  generate: async (): Promise<GenerateAlertsResponse> => {
    const response = await apiClient.post<GenerateAlertsResponse>(
      '/alerts/generate'
    );
    return response.data;
  },
};
